import { createUseStyles } from 'react-jss';
import { Spacings } from '@picsart/design-system/foundation/spacings';
import { CornerRadius } from '@picsart/design-system/foundation/cornerRadius';
import {
  BackgroundColors,
  AbsoluteWhiteColors,
} from '@picsart/design-system/foundation/colors';
import { Effects } from '@picsart/design-system/foundation/effects';
import { FontSizes } from '@picsart/design-system/foundation/typography';
import { Easings } from '@picsart/design-system/foundation/easings';
import { canHover } from '@picsart/design-system/styleUtils/hoverTouch';

// Card-internal stacking order. DS `StackingContext` tokens are app-level
// (>=1000); the card subtree keeps its own small layer indices.
//   SKELETON      (loading shimmer behind everything)
//   VIDEO_BADGE   (small "video" badge in corner)
//   MUTE_BUTTON   (overlay button — sits above all card content)
const CARD_Z_SKELETON = 0;
const CARD_Z_VIDEO_BADGE = 3;
const CARD_Z_MUTE_BUTTON = 6;

const useStyles = createUseStyles(
  {
    // Skeleton shimmer animation - GPU accelerated
    '@keyframes shimmer': {
      '0%': {
        transform: 'translateX(-100%)',
      },
      '100%': {
        transform: 'translateX(100%)',
      },
    },
    image: {
      display: 'block',
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      pointerEvents: 'none',
      opacity: 0,
      transition: `opacity 0.3s ${Easings.easeOut}`,
    },
    /**
     * A mosaic has no single `onLoad` to gate the fade-in on, so a hidden copy
     * of the first image reports it. Zero-sized rather than `display: none`,
     * which would stop the browser fetching it and the event would never fire.
     */
    mosaicLoadProbe: {
      position: 'absolute',
      width: 1,
      height: 1,
      opacity: 0,
      pointerEvents: 'none',
    },
    imageLoaded: {
      opacity: 1,
    },
    video: {
      display: 'block',
      objectFit: 'cover',
      width: '100%',
      height: '100%',
      position: 'absolute',
      top: 0,
      left: 0,
      pointerEvents: 'none',
      opacity: 0,
      transition: `opacity 0.3s ${Easings.easeOut}`,
      // Hide native video controls on Android/Chrome
      '&::-webkit-media-controls': {
        display: 'none !important',
      },
      '&::-webkit-media-controls-play-button': {
        display: 'none !important',
      },
      '&::-webkit-media-controls-start-playback-button': {
        display: 'none !important',
      },
    },
    videoLoaded: {
      opacity: 1,
    },
    // Unmounted once media loads. Fading it out instead would keep the shimmer
    // animating and its layer promoted for the rest of the session.
    mediaLoadingSkeleton: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: CARD_Z_SKELETON,
      backgroundColor: BackgroundColors.base,
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `linear-gradient(90deg, transparent 0%, ${BackgroundColors.tint2} 50%, transparent 100%)`,
        animation: `$shimmer 1.2s ${Easings.easeInOut} infinite`,
        willChange: 'transform',
      },
    },
    videoIcon: {
      position: 'absolute',
      top: Spacings.s8,
      right: Spacings.s8,
      width: 24,
      height: 24,
      backgroundColor: Effects.alpha50,
      borderRadius: CornerRadius.r4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: AbsoluteWhiteColors.base.default,
      fontSize: FontSizes.t3,
      pointerEvents: 'none',
      zIndex: CARD_Z_VIDEO_BADGE,
    },
    // Positioning wrapper for the mute toggle InlineButton overlay
    muteButtonWrapper: {
      position: 'absolute',
      bottom: Spacings.s8,
      right: Spacings.s8,
      zIndex: CARD_Z_MUTE_BUTTON,
      display: 'inline-flex',
    },
    // Opt-in top-right placement (e.g. dance cards, where the title sits bottom-left)
    muteButtonWrapperTopRight: {
      top: Spacings.s8,
      bottom: 'auto',
    },
    iconButton: {
      width: 32,
      height: 32,
      flex: '0 0 32px',
      borderRadius: CornerRadius.r8,
      backgroundColor: Effects.alpha50,
      color: AbsoluteWhiteColors.base.default,
      transition: `background-color 0.15s ${Easings.easeOut}, transform 0.15s ${Easings.easeOut}`,
      [canHover]: {
        '&:hover': {
          backgroundColor: Effects.alpha80,
          transform: 'scale(1.1)',
        },
      },
      '&:active': {
        transform: 'scale(0.95)',
      },
    },
  },
  { name: 'card-media' }
);

export default useStyles;
